import type { RedactedRollupExplanation, StatusValue } from 'types/spherePoints';

type Translate = (key: string, options?: Record<string, unknown>) => string;

const KEY = 'statusFactors.summary.rollup.sentence';

interface ClauseContext {
  explanation: RedactedRollupExplanation;
  t: Translate;
  statusLabel: (status: StatusValue) => string;
  weighting: string;
}

function decidedClause({ explanation, t }: ClauseContext): string {
  const { decidingMember, counts } = explanation;
  if (decidingMember?.kind === 'visible') {
    const key = explanation.floorExcludedAll ? 'floorExcludedEveryone' : 'decidedVisible';
    return t(`${KEY}.${key}`, { name: decidingMember.name, measured: counts.measured });
  }
  if (decidingMember?.kind === 'hidden') {
    return t(`${KEY}.decidedHidden`, { measured: counts.measured });
  }
  return t(`${KEY}.decidedOmitted`, { measured: counts.measured });
}

function modalClause({ explanation, t, statusLabel, weighting }: ClauseContext): string {
  const { tiedStatuses, counts } = explanation;
  if (!tiedStatuses || tiedStatuses.length < 2) {
    return t(`${KEY}.modal`, { measured: counts.measured, weighting });
  }
  const labels = tiedStatuses.map(statusLabel);
  const key = tiedStatuses.length === 2 ? 'modalTie' : 'modalTieMany';
  return t(`${KEY}.${key}`, { a: labels.slice(0, -1).join(', '), b: labels.at(-1), measured: counts.measured, weighting });
}

/** The clause that names the method and the cause; null for a branch with nothing to say. */
function leadClause(context: ClauseContext): string | null {
  const { explanation, t, statusLabel, weighting } = context;
  const { counts, coverageFloor } = explanation;
  switch (explanation.branch) {
    case 'decided':
      return decidedClause(context);
    case 'blend':
      return t(`${KEY}.blend`, { measured: counts.measured, weighting });
    case 'modal':
      return modalClause(context);
    case 'coverage_floor':
      return coverageFloor
        ? t(`${KEY}.coverageFloor`, {
            reporting: coverageFloor.reporting,
            total: coverageFloor.total,
            pct: Math.round(coverageFloor.minCoverage * 100),
            status: statusLabel(coverageFloor.substitutedStatus),
          })
        : null;
    case 'empty_population':
      return t(`${KEY}.emptyPopulation`, { status: statusLabel(explanation.emptyBehavior ?? 'unknown') });
    case 'nothing_measured':
      return t(`${KEY}.nothingMeasured`, { count: counts.total });
    case 'zero_total_influence':
      return t(`${KEY}.zeroTotalInfluence`, { measured: counts.measured });
    default:
      return null;
  }
}

/** One clause per further cause present: the weight floor, unmeasured, hidden and broken members, the read-through. */
function trailingClauses({ explanation, t }: ClauseContext): string[] {
  const { branch, method, counts } = explanation;
  const clauses: string[] = [];
  if (branch === 'decided' && method === 'worst' && !explanation.floorExcludedAll && counts.underFloor > 0) {
    clauses.push(t(`${KEY}.underFloor`, { count: counts.underFloor }));
  }
  const leadCoversUnmeasured = branch === 'nothing_measured' || branch === 'empty_population';
  if (counts.unmeasured > 0 && !leadCoversUnmeasured) {
    clauses.push(t(`${KEY}.unmeasured`, { count: counts.unmeasured }));
  }
  if (counts.hidden > 0) {
    clauses.push(t(`${KEY}.hidden`, { count: counts.hidden }));
  }
  if (counts.contributionBroken > 0) {
    clauses.push(t(`${KEY}.contributionBroken`, { count: counts.contributionBroken }));
  }
  if (explanation.readThrough) {
    clauses.push(t(`${KEY}.readThrough`, { perspective: explanation.readThrough }));
  }
  return clauses;
}

/**
 * The "why is it this colour?" sentence, composed from the redacted explanation: one lead clause by
 * branch, trailing clauses for each cause that is present, and the set's READS AS sentence last.
 * Every count is passed as a number so the locale renders it as digits.
 */
export function buildWhySentence(
  explanation: RedactedRollupExplanation,
  t: Translate,
  readsAs?: string | null
): string {
  const context: ClauseContext = {
    explanation,
    t,
    statusLabel: (status) => t(`statusFactors.roster.statusValue.${status}`),
    weighting: t(`${KEY}.weighting.${explanation.weighting}`),
  };
  const lead = leadClause(context);
  const knownBranch = lead !== null || explanation.branch === 'coverage_floor';
  if (!knownBranch) return '';
  const clauses = [...(lead === null ? [] : [lead]), ...trailingClauses(context)];
  if (readsAs) {
    clauses.push(t(`${KEY}.setDefinition`, { readsAs }));
  }
  return clauses.join(' ');
}
